import { isEmpty } from 'lodash';

import { SmoothingAlgorithm } from '@neptune/shared/smoothing-domain';
import { AxisRange, AxisScale, XAxisMode } from '@neptune/shared/widgets-domain';

import { Complete } from 'common/utility-types';
import { AttributeDefinition } from 'domain/experiment/attribute';

import type { ConflictDictionary } from './charts-conflicts';
import type { ErrorBandsTemplateConfig } from './error-band-templates';
import { ErrorBandsConfig } from './error-bands';
import { ReferenceLineConfigById } from './reference-lines';

export type JsonCompatibleAxisRange = [number | undefined | null, number | undefined | null];

export type OutliersDisplayMode = 'none' | 'outline';

export function removeNullsFromAxisRange(
  range: JsonCompatibleAxisRange | undefined | null,
): AxisRange | undefined {
  return (range && [range[0] ?? undefined, range[1] ?? undefined]) ?? undefined;
}

export function getXAxisModeHandlingLegacyCustomExpressionAsStepMode(
  options?: Pick<ChartConfigurationSettings, 'customXExpression' | 'xAxisMode'>,
) {
  if (!options) {
    return undefined;
  }

  return options.customXExpression && options.xAxisMode === 'step'
    ? 'customExpressionSeries'
    : options.xAxisMode;
}

export function getShowOriginalLineOnSmoothing(value: boolean | undefined) {
  return value ?? true;
}

export function shouldShowOriginalLineOnSmoothing(
  smoothingEnabled: boolean,
  showOriginalLineOnSmoothing: boolean | undefined,
) {
  return smoothingEnabled && getShowOriginalLineOnSmoothing(showOriginalLineOnSmoothing);
}

export function areLocalSettingsSet(config: Partial<ChartConfigurationSettings>) {
  const { customXExpression, customXExpressionName, ...remainingSettings } = config;

  // Handle edge case where customX fields were saved as empty strings
  if (!isEmpty(customXExpression) && !isEmpty(customXExpressionName)) {
    return true;
  }

  for (const [key, value] of Object.entries(remainingSettings)) {
    if (Object.hasOwnProperty.call(DEFAULT_CHARTS_GLOBAL_CONFIG, key)) {
      const defaultValue =
        DEFAULT_CHARTS_GLOBAL_CONFIG[key as keyof typeof DEFAULT_CHARTS_GLOBAL_CONFIG];

      if (value !== undefined && value !== defaultValue) {
        return true;
      }
    }
  }

  return false;
}

// TODO migrate to using the one from widget-configuration.ts
export const isAxisScale = (input: string): input is AxisScale =>
  input === 'linear' || input === 'logarithmic' || input === 'symlog';

// TODO migrate to using the one from widget-configuration.ts
export type AxisRangeHandlerParams = {
  min: number | undefined;
  max: number | undefined;
};

export type ChartConfigurationProps = ChartConfigurationSettings & {
  onSetYLogScale: (scale?: AxisScale) => void;
  onSetXLogScale: (scale?: AxisScale) => void;
  setXAxisMode?: (xAxisMode?: XAxisMode) => void;
  setXAxisMetric?: (xAxisMetric?: AttributeDefinition) => void;
  setYAxisRange?: (values: AxisRangeHandlerParams) => void;
  setXAxisRange?: (values: AxisRangeHandlerParams) => void;
  onNormalizeToFirstValueChange?: (normalizeToFirstValue: boolean) => void;
  selectedAttributes?: AttributeDefinition[];
  conflicts?: ConflictDictionary;
};

type ChartLocalConfigurationSettings = {
  averaging?: boolean;
  normalizeToFirstValue?: boolean;
  xAxisScale?: AxisScale;
  yAxisScale?: AxisScale;
  xAxisMode?: XAxisMode;
  xAxisMetric?: AttributeDefinition;
  yAxisRange?: AxisRange;
  xAxisRange?: AxisRange;
  customXExpression?: string;
  customXExpressionName?: string;
  smoothing?: number;
  showCompletion?: boolean;
  showOriginalLineOnSmoothing?: boolean;
  smoothingAlgorithm?: SmoothingAlgorithm;
  customErrorBandsEnabled?: boolean;
  customErrorBandsConfigByMetricName?: Record<string, ErrorBandsConfig>;
  customErrorBandsConfigForAttributePattern?: ErrorBandsTemplateConfig;
  referenceLinesEnabled?: boolean;
  referenceLinesConfigById?: ReferenceLineConfigById;
  outliersDisplayMode?: OutliersDisplayMode;
};

export type ChartConfigurationSettings = ChartLocalConfigurationSettings & {
  metricsStepsRange?: AxisRange;
};

const DEFAULT_CHART_LOCAL_CONFIG: Complete<ChartLocalConfigurationSettings> = {
  smoothing: undefined,
  smoothingAlgorithm: undefined,
  averaging: false,
  xAxisMode: undefined,
  xAxisScale: undefined,
  yAxisScale: undefined,
  xAxisMetric: undefined,
  yAxisRange: undefined,
  xAxisRange: undefined,
  customXExpression: undefined,
  customXExpressionName: undefined,
  showCompletion: undefined,
  normalizeToFirstValue: false,
  showOriginalLineOnSmoothing: undefined,
  customErrorBandsEnabled: undefined,
  customErrorBandsConfigByMetricName: undefined,
  customErrorBandsConfigForAttributePattern: undefined,
  referenceLinesEnabled: undefined,
  referenceLinesConfigById: undefined,
  outliersDisplayMode: undefined,
};

export const DEFAULT_CHARTS_GLOBAL_CONFIG: Partial<ChartLocalConfigurationSettings> = {
  smoothing: undefined,
  smoothingAlgorithm: undefined,
  averaging: false,
  xAxisMode: 'step',
  xAxisScale: undefined,
  yAxisScale: undefined,
  xAxisMetric: undefined,
  yAxisRange: undefined,
  xAxisRange: undefined,
  showCompletion: false,
  customXExpression: undefined,
  customXExpressionName: undefined,
  showOriginalLineOnSmoothing: true,
  outliersDisplayMode: 'none',
};

export const DEFAULT_CHART_CONFIG: Complete<ChartConfigurationSettings> = {
  ...DEFAULT_CHART_LOCAL_CONFIG,
  metricsStepsRange: undefined,
  normalizeToFirstValue: false,
};

export const prefillAttributes: AttributeDefinition[] = [
  { name: 'step', type: 'floatSeries', predefined: true },
  { name: 'relative time', type: 'datetime', predefined: true },
  { name: 'absolute time', type: 'datetime', predefined: true },
];

export const attributeNameToXAxisMode: Record<string, XAxisMode | undefined> = {
  step: 'step',
  'relative time': 'relativeTime',
  'absolute time': 'absoluteTime',
};

const xAxisModeToAttributeName: Record<string, string | undefined> = {
  step: 'step',
  relativeTime: 'relative time',
  absoluteTime: 'absolute time',
};

export function getSelectedMetricName(xAxisMode: XAxisMode, xAxisMetric?: AttributeDefinition) {
  if (xAxisModeToAttributeName[xAxisMode]) {
    return xAxisModeToAttributeName[xAxisMode];
  }

  return xAxisMetric?.name;
}
